import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';

const THEME_STORAGE_KEY = 'theme-preference';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const getStoredPreference = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'device') return stored;
  } catch (e) {}
  return 'device';
};

const getEffectiveTheme = (preference) => {
  if (preference === 'light') return 'light';
  if (preference === 'dark') return 'dark';
  if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

export const ThemeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(getStoredPreference);
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  const effectiveTheme = useMemo(
    () => (preference === 'device' ? (systemDark ? 'dark' : 'light') : preference),
    [preference, systemDark]
  );

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(!!e.matches);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveTheme);
  }, [effectiveTheme]);

  const setPreference = (value) => {
    if (value !== 'light' && value !== 'dark' && value !== 'device') return;
    setPreferenceState(value);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch (e) {}
  };

  const value = useMemo(
    () => ({
      theme: effectiveTheme,
      preference,
      setPreference,
      isDark: effectiveTheme === 'dark'
    }),
    [effectiveTheme, preference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
